import { PointAttributeName } from '@pnext/three-loader';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai/utils';

/**
 * What a LOADED point cloud can actually do, as opposed to what the UI would
 * like it to do.
 *
 * Born from the clearance render modes: `highlight` / `only` colour and filter
 * by LAS class 13, but every clearance cloud converted before
 * `processClearance` started passing `attributes: ['Clearance',
 * 'Classification']` to PotreeConverter has NO classification attribute in its
 * octree. Potree hands the shader an all-zero classification buffer for those,
 * so `highlight` paints the whole cloud grey and `only` draws nothing at all —
 * a blank viewer with no error anywhere.
 *
 * Deliberately an object rather than a bare boolean: rgb / intensity /
 * gps-time presence are the same kind of question and should join here rather
 * than each growing their own atom.
 */
export interface PointcloudCapabilities {
  hasClassification: boolean;
}

/** The one attribute name we care about so far, lower-cased for comparison.
 *  Matched EXACTLY (after lower-casing) rather than by `includes`, so a
 *  neighbouring dimension like `classificationConfidence` cannot pass for it. */
const CLASSIFICATION_NAME = 'classification';

interface AttributeLike {
  name?: unknown;
}

interface PointAttributesLike {
  attributes?: unknown;
  /** Set by the v1 loader when each node on disk is a whole LAZ/LAS file. */
  compressedFormat?: unknown;
}

interface LoaderLike {
  metadata?: { attributes?: unknown } | null;
}

interface GeometryLike {
  pointAttributes?: unknown;
  loader?: unknown;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * True when `name` is how one of the loaders spells "classification".
 *
 * Three spellings have to be tolerated, which is why this is not a `===`:
 *
 *  - **v2 (`metadata.json`)** — `OctreeLoader.parseAttributes` copies the name
 *    straight out of the JSON, so it is whatever PotreeConverter wrote:
 *    `classification` in practice, but the `--attributes Classification` we
 *    pass is the capitalised form and the converter's casing is not ours to
 *    guarantee. Compared case-insensitively.
 *  - **v1 (`cloud.js`)** — the name is the NUMERIC `PointAttributeName`
 *    enum (`CLASSIFICATION === 7`), not a string at all. A string-only check
 *    silently reports "no classification" for every v1 cloud, which is exactly
 *    the false negative this whole seam exists to avoid.
 */
const isClassificationName = (name: unknown): boolean => {
  if (typeof name === 'number') return name === PointAttributeName.CLASSIFICATION;
  if (typeof name !== 'string') return false;
  return name.toLowerCase() === CLASSIFICATION_NAME;
};

const listHasClassification = (attributes: unknown): boolean =>
  Array.isArray(attributes) && attributes.some((a: AttributeLike) => isClassificationName(a?.name));

/**
 * Sniff a loaded cloud's `pcoGeometry` for a classification attribute.
 *
 * Pure and structurally typed on purpose: the two Potree versions expose
 * genuinely different shapes (`PointCloudOctreeGeometry` vs `OctreeGeometry`),
 * and the v2 loader type only ships in a `build/declarations` subpath. Reading
 * defensively costs nothing here and keeps the detector unit-testable without
 * a WebGL context or a real octree.
 *
 * Order matters — most authoritative source first:
 *
 *  1. v2 `loader.metadata.attributes` — the converter's own manifest.
 *  2. `pointAttributes.attributes` — what the loader parsed, for both versions.
 *  3. `pointAttributes.compressedFormat` — v1 LAZ/LAS-per-node clouds carry an
 *     EMPTY attribute list by design (the schema lives in each node's own LAS
 *     header). Classification is a mandatory field of every LAS point format,
 *     so these are reported as capable rather than as missing.
 *
 * Unrecognisable input answers `false`, which disables a feature rather than
 * blanking a viewer — the safe direction to be wrong in.
 */
export const detectPointcloudCapabilities = (geometry: unknown): PointcloudCapabilities => {
  if (!isRecord(geometry)) return { hasClassification: false };
  const geo = geometry as GeometryLike;

  const loader = isRecord(geo.loader) ? (geo.loader as LoaderLike) : null;
  if (listHasClassification(loader?.metadata?.attributes)) return { hasClassification: true };

  const pointAttributes = isRecord(geo.pointAttributes) ? (geo.pointAttributes as PointAttributesLike) : null;
  if (listHasClassification(pointAttributes?.attributes)) return { hasClassification: true };

  const compressed = pointAttributes?.compressedFormat;
  if (compressed === 'LAZ' || compressed === 'LAS') return { hasClassification: true };

  return { hasClassification: false };
};

/**
 * Per-layer capabilities, keyed by layer id. `null` means "no cloud has
 * finished loading for this layer yet" — which is NOT the same as "this cloud
 * has no classification", and consumers must keep the two apart
 * (`viewModeUnavailableReason` in `lib/clearance/viewMode.ts` does).
 *
 * Not persisted: it describes the bytes currently in memory, and a cloud
 * re-converted between sessions would make a stored answer a lie.
 */
export const pointcloudCapabilitiesAtom = atomFamily((_layerId: string) => atom<PointcloudCapabilities | null>(null));

/**
 * Write-only funnel so a component can record capabilities for MANY layers
 * from one `useSetAtom` — `useSetAtom(pointcloudCapabilitiesAtom(id))` cannot
 * be called in a loop over a layer list without breaking the rules of hooks.
 */
export const setPointcloudCapabilitiesAtom = atom(
  null,
  (_get, set, payload: { layerId: string; capabilities: PointcloudCapabilities }) => {
    set(pointcloudCapabilitiesAtom(payload.layerId), payload.capabilities);
  },
);

/** Key for "no layer selected" — a stable atom nothing ever writes to. */
const NO_LAYER = '';

/**
 * `true` / `false` once the layer's cloud has loaded, `undefined` while it
 * hasn't (or when there is no layer to ask about).
 *
 * Safe to call unconditionally with a nullable id, which is what lets a dock
 * read it above its own "is a layer selected?" branch.
 */
export const usePointcloudHasClassification = (layerId: string | null | undefined): boolean | undefined => {
  const capabilities = useAtomValue(pointcloudCapabilitiesAtom(layerId ?? NO_LAYER));
  if (!layerId) return undefined;
  return capabilities?.hasClassification;
};
